import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { ContributionEntry } from '../types';

interface ContributionsTableProps {
  contributions: ContributionEntry[];
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  readOnly?: boolean;
  showGroup?: boolean;
}

const ContributionsTable = ({
  contributions,
  onApprove,
  onReject,
  readOnly = false,
  showGroup = false,
}: ContributionsTableProps) => {
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VT`;
  };

  return (
    <div className="bg-card border border-border rounded-md shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Recent Contributions
          </h2>
          <button className="text-sm text-primary hover:text-primary/80 transition-interactive font-medium">
            View All
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Member
              </th>
              {showGroup && (
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Group
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Recorded By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contributions.map((entry) => (
              <tr
                key={entry.id}
                className={`hover:bg-muted transition-interactive ${
                  selectedEntry === entry.id ? 'bg-primary/5' : ''
                }`}
                onClick={() => setSelectedEntry(entry.id)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <Image
                      src={entry.memberAvatar}
                      alt={entry.memberAvatarAlt}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm font-medium text-foreground">
                      {entry.memberName}
                    </span>
                  </div>
                </td>
                {showGroup && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-muted-foreground">
                      {entry.groupName || 'Unknown Group'}
                    </span>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(entry.amount)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(entry.date)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-muted-foreground">
                    {entry.recordedBy}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                      entry.verificationStatus === 'pending' ?'bg-warning/10 text-warning'
                        : entry.verificationStatus === 'verified' ?'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                    }`}
                  >
                    {entry.verificationStatus.charAt(0).toUpperCase() +
                      entry.verificationStatus.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {entry.verificationStatus === 'pending' && !readOnly && onApprove && onReject && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="success"
                        size="xs"
                        iconName="Check"
                        onClick={(e) => {
                          e.stopPropagation();
                          onApprove(entry.id);
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="xs"
                        iconName="X"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReject(entry.id);
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                  {entry.verificationStatus === 'pending' && readOnly && (
                    <span className="text-xs text-muted-foreground">View Only</span>
                  )}
                  {entry.verificationStatus === 'verified' && (
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContributionsTable;